import { useEffect, useRef, useState, type RefObject } from 'react';
import { MEDIA_QUERIES } from '@shared/constants';

const CONVERSION_BAR_TOP_MARGIN = 32;
const CONTENT_PADDING = 32; // $spacing-4

const isDesktopWidth = (): boolean => {
  return window.innerWidth >= MEDIA_QUERIES.LARGE_UP_BREAKPOINT;
};

const getScrollTop = (): number => {
  return window.scrollY || document.documentElement.scrollTop;
};

const calculateRightPosition = (): number => {
  const windowWidth = window.innerWidth;
  const bodyWidth = Math.min(windowWidth, MEDIA_QUERIES.LARGE_UP_BREAKPOINT);
  const sideMargin = (windowWidth - bodyWidth) / 2;

  // If window width is >= 1264px (1200px + 32px + 32px), no padding
  if (windowWidth >= MEDIA_QUERIES.LARGE_UP_CONTENT_EXPAND_WIDTH) {
    return sideMargin;
  }

  return sideMargin + CONTENT_PADDING;
};

const setConversionBarPosition = (top: string, right: string): void => {
  document.documentElement.style.setProperty('--conversion-bar-top', top);
  document.documentElement.style.setProperty('--conversion-bar-right', right);
};

const calculateInitialTopValue = (wrapperRef: RefObject<HTMLDivElement>): number | null => {
  if (!wrapperRef.current) {
    return null;
  }

  const rect = wrapperRef.current.getBoundingClientRect();
  const scrollTop = getScrollTop();
  const initialPosition = rect.top + scrollTop;
  return Math.max(initialPosition, CONVERSION_BAR_TOP_MARGIN);
};

const updateConversionBarPosition = (currentInitialTop: number): void => {
  const scrollTop = getScrollTop();
  const rightPosition = calculateRightPosition();
  const topPosition =
    scrollTop >= currentInitialTop - CONVERSION_BAR_TOP_MARGIN
      ? `${CONVERSION_BAR_TOP_MARGIN}px`
      : `${currentInitialTop - scrollTop}px`;

  setConversionBarPosition(topPosition, `${rightPosition}px`);
};

/**
 * When the layout above the conversion bar changes (e.g. CreatorBanner mounts),
 * pass a new value so the conversion bar position is recalculated to not overlap the new content.
 */
export const useStickyDesktopConversionBar = (layoutChangeKey?: unknown) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [initialTop, setInitialTop] = useState<number>(CONVERSION_BAR_TOP_MARGIN);
  const [isPositionCalculated, setIsPositionCalculated] = useState<boolean>(false);
  const initialTopCalculated = useRef<boolean>(false);
  const initialTopRef = useRef<number>(CONVERSION_BAR_TOP_MARGIN);
  const prevLayoutChangeKeyRef = useRef<unknown>(layoutChangeKey);

  if (prevLayoutChangeKeyRef.current !== layoutChangeKey) {
    prevLayoutChangeKeyRef.current = layoutChangeKey;
    initialTopCalculated.current = false;
  }

  useEffect(() => {
    const calculateInitialPosition = () => {
      if (!wrapperRef.current) {
        return;
      }

      if (!isDesktopWidth()) {
        setIsPositionCalculated(true);
        return;
      }

      // Only calculate initialTop once when scroll is at top, or if not calculated yet
      if (!initialTopCalculated.current) {
        const calculatedTop = calculateInitialTopValue(wrapperRef);
        if (calculatedTop !== null) {
          setInitialTop(calculatedTop);
          initialTopRef.current = calculatedTop;
          initialTopCalculated.current = true;
        }
      }

      updateConversionBarPosition(initialTopRef.current);
      setIsPositionCalculated(true);
    };

    const handleResize = () => {
      // If initialTop hasn't been calculated yet and wrapperRef is available, calculate it
      if (!initialTopCalculated.current && wrapperRef.current && isDesktopWidth()) {
        const calculatedTop = calculateInitialTopValue(wrapperRef);
        if (calculatedTop !== null) {
          setInitialTop(calculatedTop);
          initialTopRef.current = calculatedTop;
          initialTopCalculated.current = true;
        }
      }

      // On resize, only update position, don't recalculate initialTop if already calculated
      if (wrapperRef.current && isDesktopWidth()) {
        updateConversionBarPosition(initialTopRef.current);
      }
    };

    const handleScroll = () => {
      if (wrapperRef.current && isDesktopWidth()) {
        updateConversionBarPosition(initialTopRef.current);
      }
    };

    calculateInitialPosition();

    window.addEventListener('resize', handleResize);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [initialTop, layoutChangeKey]);

  return { wrapperRef, isPositionCalculated };
};
